import { useState } from 'react';
import { Image as ImageIcon, Palette, Link2, HelpCircle, MoreVertical, RefreshCw, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Input,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui';
import type { MerchantAsset } from '@/types/merchant-asset';

const GRADIENTS = [
  'linear-gradient(135deg, hsl(72 60% 42%), hsl(96 45% 28%))',
  'linear-gradient(135deg, hsl(222 47% 30%), hsl(217 60% 52%))',
  'linear-gradient(135deg, hsl(25 80% 52%), hsl(8 70% 46%))',
  'linear-gradient(135deg, hsl(272 45% 48%), hsl(222 47% 32%))',
  'linear-gradient(135deg, hsl(190 55% 40%), hsl(222 47% 28%))',
];
function gradientFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
}

function Preview({ asset }: { asset: MerchantAsset }) {
  const base = 'flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border';
  if (asset.type === 'stylesheet') {
    return (
      <div className={`${base} relative bg-card`}>
        <div className="absolute inset-0 flex flex-col">
          <span className="flex-1" style={{ background: 'hsl(72 100% 34%)' }} />
          <span className="flex-1" style={{ background: 'hsl(222 47% 11%)' }} />
          <span className="flex-1 bg-secondary" />
        </div>
        <Palette className="relative h-5 w-5 text-white/90 drop-shadow" />
      </div>
    );
  }
  // background / header / logo → Bild-Platzhalter
  return (
    <div className={`${base} relative`} style={{ backgroundImage: gradientFor(asset.assetId) }}>
      <ImageIcon className="h-5 w-5 text-white/80" />
    </div>
  );
}

function UsageBadge({ asset }: { asset: MerchantAsset }) {
  const { t } = useTranslation('merchant-assets');
  const usageCount = asset.usedBy.length;

  // Degradation (Spec §3): Nutzung gerade nicht ermittelbar → kein „in 0 Diensten".
  if (asset.usageUnavailable) {
    return (
      <Badge variant="outline" className="shrink-0 gap-1 text-muted-foreground">
        <HelpCircle className="h-3 w-3" />
        {t('row.usageUnknown')}
      </Badge>
    );
  }

  if (usageCount === 0) {
    return (
      <Badge variant="outline" className="shrink-0 text-muted-foreground">
        {t('row.unused')}
      </Badge>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="secondary"
          className="shrink-0 cursor-default gap-1 font-medium"
          style={usageCount > 1 ? { color: 'hsl(72 100% 24%)', background: 'hsl(72 40% 92%)' } : undefined}
        >
          <Link2 className="h-3 w-3" />
          {t('row.inServices', { count: usageCount })}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="mb-1 font-medium">{t('row.usedIn')}</p>
        <ul className="space-y-0.5">
          {asset.usedBy.map((u) => (
            <li key={u.serviceId} className="text-xs">{u.displayName}</li>
          ))}
        </ul>
        {usageCount > 1 && (
          <p className="mt-1.5 border-t border-border/50 pt-1.5 text-xs text-muted-foreground">
            {t('row.propagationNote', { count: usageCount })}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

interface AssetRowProps {
  asset: MerchantAsset;
  /** Issue #334: hide all write affordances for read-only roles (merchant_viewer). */
  canWrite: boolean;
  /** Inline rename (Spec §4): the new name is committed in the row, not a dialog. */
  onRename: (asset: MerchantAsset, newName: string) => void;
  onReplaceFile: (asset: MerchantAsset) => void;
  onDelete: (asset: MerchantAsset) => void;
}

export function AssetRow({ asset, canWrite, onRename, onReplaceFile, onDelete }: AssetRowProps) {
  const { t, i18n } = useTranslation('merchant-assets');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(asset.name);

  const updated = new Date(asset.updatedAt).toLocaleDateString(i18n.language, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const startEdit = () => {
    setDraft(asset.name);
    setEditing(true);
  };
  const commit = () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next.length <= 200 && next !== asset.name) {
      onRename(asset, next);
    }
  };
  const cancel = () => {
    setEditing(false);
    setDraft(asset.name);
  };

  return (
    <div className="group flex items-center gap-4 px-4 py-3 transition-colors hover:bg-muted/40">
      <Preview asset={asset} />

      <div className="min-w-0 flex-1">
        {editing ? (
          <Input
            autoFocus
            value={draft}
            maxLength={200}
            aria-label={t('row.rename')}
            className="h-7"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') cancel();
            }}
            onBlur={commit}
          />
        ) : (
          <>
            <div className="flex items-center gap-1.5">
              <span className="truncate font-medium text-foreground">{asset.name}</span>
              {/* Inline-Rename (Spec §4): direkter Stift — kein Dropdown-Fokus-Konflikt. */}
              {canWrite && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('row.rename')}
                  className="h-6 w-6 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 coarse:min-h-11 coarse:min-w-11 coarse:opacity-100"
                  onClick={startEdit}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              )}
            </div>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">{asset.currentFileUrl}</p>
          </>
        )}
      </div>

      <span className="hidden shrink-0 text-xs text-muted-foreground sm:block">
        {t('row.updated', { date: updated })}
      </span>

      {/* Wiederverwendungs-Signal (ABGELEITET im BFF) */}
      <UsageBadge asset={asset} />

      {canWrite && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 text-muted-foreground opacity-60 transition-opacity group-hover:opacity-100"
              aria-label={t('row.actions')}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onSelect={() => onReplaceFile(asset)}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('row.updateFile')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onDelete(asset)} className="text-destructive focus:text-destructive">
              <Trash2 className="mr-2 h-4 w-4" />
              {t('row.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
